import { useCallback, useEffect, useState } from 'react';
import { ecorpResources } from '@command-center/admin-ecorp';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@command-center/ui-system';
import { Mail, Pencil, Phone, Plus, UserRound } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { getRecord, listRecords, updateRecord, type PortalRecord } from '../api';
import { display, useToast } from '../lib';
import { RecordDialog, ResourceView } from '../ResourceView';
import { ContactDetailDialog, ContactFormDialog } from './ContactsView';
import { contactEmailHref, contactPhoneHref, relationItems } from './contact-utils';

export function ClientsView({ onContext }: { onContext(value: Record<string, unknown>): void }) {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<PortalRecord>();
  const [allClients, setAllClients] = useState<PortalRecord[]>([]);
  const [editingClient, setEditingClient] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [contact, setContact] = useState<PortalRecord>();
  const [editingContact, setEditingContact] = useState(false);
  const openAddContact = useCallback(() => setAddingContact(true), []);

  useEffect(() => {
    void listRecords(
      'clients',
      new URLSearchParams({ pageSize: '100', sortField: 'Client Name', sortDirection: 'asc' }),
    )
      .then((page) => setAllClients(page.items))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (id) void openClient(id);
  }, [id]);

  async function openClient(recordId: string) {
    const payload = await getRecord('clients', recordId);
    setClient(payload.item);
  }
  async function refreshClient() {
    if (client) await openClient(client.id);
  }
  async function openContact(recordId: string) {
    const payload = await getRecord('contacts', recordId);
    setContact(payload.item);
  }

  return (
    <>
      <ResourceView
        resource={ecorpResources.clients}
        onContext={onContext}
        onOpenRecord={(item) => void openClient(item.id)}
        headerTitle={id ? display(client?.fields['Client Name']) || 'Client' : undefined}
        headerSubtitle={id ? 'Client details, contacts, and project history.' : undefined}
        createLabel={id ? 'Add Contact' : undefined}
        onCreate={id ? openAddContact : undefined}
      />
      <ClientDetailDialog
        item={client}
        open={Boolean(client) && !editingClient && !addingContact && !contact}
        onClose={() => setClient(undefined)}
        onEdit={() => setEditingClient(true)}
        onAddContact={() => setAddingContact(true)}
        onOpenContact={(recordId) => void openContact(recordId)}
        onChanged={() => void refreshClient()}
      />
      <RecordDialog
        resource={ecorpResources.clients}
        item={client}
        open={Boolean(client) && editingClient}
        editing
        onEditing={setEditingClient}
        onClose={() => setEditingClient(false)}
        onSaved={(item) => {
          setClient(item);
          setEditingClient(false);
          void refreshClient();
        }}
      />
      <ContactFormDialog
        open={Boolean(client) && addingContact}
        initialClientId={client?.id}
        clients={allClients}
        onClose={() => setAddingContact(false)}
        onSaved={() => {
          setAddingContact(false);
          void refreshClient();
        }}
      />
      <ContactDetailDialog
        item={contact}
        open={Boolean(contact) && !editingContact}
        onClose={() => setContact(undefined)}
        onEdit={() => setEditingContact(true)}
        onDeleted={() => {
          setContact(undefined);
          void refreshClient();
        }}
      />
      <ContactFormDialog
        open={Boolean(contact) && editingContact}
        contact={contact}
        clients={allClients}
        onClose={() => setEditingContact(false)}
        onSaved={(item) => {
          setEditingContact(false);
          void openContact(item.id);
          void refreshClient();
        }}
      />
    </>
  );
}

function ClientDetailDialog({
  item,
  open,
  onClose,
  onEdit,
  onAddContact,
  onOpenContact,
  onChanged,
}: {
  item?: PortalRecord;
  open: boolean;
  onClose(): void;
  onEdit(): void;
  onAddContact(): void;
  onOpenContact(id: string): void;
  onChanged(): void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const fields = item?.fields ?? {};
  const contacts = relationItems(fields.Contacts);
  const mainPoc = relationItems(fields['Main POC']);
  const email = display(fields['Business Email Address']) || display(fields['Email Address']);
  const phone = display(fields['Business Phone Number']) || display(fields['Phone Number']);
  const emailHref = contactEmailHref(email);
  const phoneHref = contactPhoneHref(phone);
  async function setMainPoc(contactId?: string) {
    if (!item || busy) return;
    setBusy(true);
    try {
      await updateRecord('clients', item.id, { 'Main POC': contactId ? [contactId] : [] });
      toast(contactId ? 'Main POC updated.' : 'Main POC cleared.');
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Main POC could not be updated.', 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{display(fields['Client Name']) || 'Client'}</DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={onAddContact}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add Contact
              </Button>
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit Client
              </Button>
              {emailHref && (
                <Button size="sm" variant="outline" asChild>
                  <a href={emailHref}>
                    <Mail className="mr-1.5 h-4 w-4" />
                    Email
                  </a>
                </Button>
              )}
              {phoneHref && (
                <Button size="sm" variant="outline" asChild>
                  <a href={phoneHref}>
                    <Phone className="mr-1.5 h-4 w-4" />
                    Call
                  </a>
                </Button>
              )}
            </div>
            <section className="rounded-xl border border-primary/30 bg-primary/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium">Main point of contact</h3>
                {mainPoc.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void setMainPoc()}
                  >
                    Clear Main POC
                  </Button>
                )}
              </div>
              {mainPoc.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {mainPoc.map((person) => (
                    <button
                      key={person.id}
                      className="inline-flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm hover:border-primary/50"
                      onClick={() => onOpenContact(person.id)}
                    >
                      <UserRound className="h-4 w-4 text-primary" />
                      {person.label}
                      <Badge>Main POC</Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">
                  No Main POC selected. Choose one from the linked Contacts below.
                </p>
              )}
            </section>
            <section>
              <div className="mb-2 flex items-center justify-between border-b pb-2">
                <h3 className="font-medium">Linked Contacts</h3>
                <span className="text-xs text-muted-foreground">
                  {contacts.length} contact{contacts.length === 1 ? '' : 's'}
                </span>
              </div>
              {contacts.length ? (
                <div className="space-y-2">
                  {contacts.map((person) => {
                    const current = mainPoc.some((candidate) => candidate.id === person.id);
                    return (
                      <div
                        key={person.id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border p-3"
                      >
                        <UserRound className="h-4 w-4 text-muted-foreground" />
                        <button
                          className="mr-auto font-medium text-primary hover:underline"
                          onClick={() => onOpenContact(person.id)}
                        >
                          {person.label}
                        </button>
                        {current ? (
                          <Badge>Main POC</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => void setMainPoc(person.id)}
                          >
                            Set Main POC
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => onOpenContact(person.id)}>
                          Open Contact
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No Contacts are linked to this Client yet.
                </p>
              )}
            </section>
            <section>
              <h3 className="mb-2 border-b pb-2 font-medium">Client details</h3>
              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ['Organization Type', display(fields['Organization Type'])],
                  ['Email', email],
                  ['Phone', phone],
                  ['Address', display(fields['Business Address']) || display(fields.Address)],
                  ['Number of Projects', display(fields['Number of Projects'])],
                  ['Last Project Date', display(fields['Last Project Date'])],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {label}
                    </dt>
                    <dd className="mt-1 text-sm">{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <section>
              <h3 className="mb-2 border-b pb-2 font-medium">Notes</h3>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {display(fields.Notes) || '—'}
              </p>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
